import { useCallback, useRef, useState, useContext, useMemo } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import clsx from 'clsx'
import styled from 'styled-components'
import { rem } from 'polished'
import CloseButton from 'components/Common/CloseButton'
import TextButton from 'LuxKit/Button/TextButton'
import CalendarMonthPicker from './CalendarMonthPicker'
import CalendarDayPicker from './CalendarDayPicker'
import CalendarDurationSelect from './CalendarDurationSelect'
import CalendarSidebarDayPicker from './CalendarSidebarDayPicker'
import IconButton from 'LuxKit/Button/IconButton'
import LineAngleLeftBIcon from 'LuxKit/Icons/line/LineAngleLeftBIcon'
import eases from 'styles/tools/eases'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import UserAnalyticsActionContext from 'contexts/userAnalyticsActionContext'
import { CALENDAR_GA_EVENT_ACTION, CALENDAR_GA_EVENT_LABEL } from 'constants/analytics'
import BodyText from 'LuxKit/Typography/BodyText'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import { groupBy, sum } from 'lib/array/arrayUtils'
import { getSelectedDay, getCalendarDates, isBedbankCalendarFetching } from 'selectors/offerPage/bedbankOfferPageSelectors'
import { sumUpOccupancies } from 'lib/offer/occupancyUtils'
import { selectIsBedbankFlightBookingEnabled, isFlightBedbankCalendarFetching } from 'selectors/flightsSelectors'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { shouldEnableCalendarRecommendations } from 'selectors/featuresSelectors'
import ErrorBoundary from 'components/Common/ErrorBoundary'
import CalendarPickerRecommendations from 'components/OfferPage/Filters/CalendarPicker/Recommendations/CalendarPickerRecommendations'
import { CalendarRecommendationsViewProvider, useCalendarRecommendationsView } from 'components/OfferPage/Filters/CalendarPicker/CalendarRecommendationsViewContext'
import CalendarPickerRecommendationsHeading from 'components/OfferPage/Filters/CalendarPicker/Recommendations/CalendarPickerRecommendationsHeading'
import OfferCalendarFlightsToggle from 'components/OfferPage/Filters/CalendarPicker/OfferCalendarFlightsToggle'
import CalendarRoomSelect from './CalendarRoomSelect'
import { displayPricingAs, getCalendarDisplayTotalPrice } from 'lib/hotels/bedbankUtils'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import Group from 'components/utils/Group'
import PriceRowPriceCaption from 'LuxKit/PricePoints/PriceRowPriceCaption'
import PriceRowPrice from 'LuxKit/PricePoints/PriceRowPrice'

const FadingBackButton = styled(IconButton)`
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.2s;

  &.visible {
    opacity: 1;
    pointer-events: auto;
  }
`

const Calendar = styled.div`
  padding: ${rem(20)} 0;
  flex-grow: 1;
  min-height: ${rem(400)};

  ${mediaQueryUp.tablet} {
    padding: ${rem(20)} ${rem(40)};
  }
`

const Header = styled(Group)`
  padding: ${rem(16)} ${rem(40)};
  box-shadow: ${props => props.theme.shadow.bottom.small};
  background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
  z-index: 2;
  border-start-start-radius: ${props => props.theme.borderRadius.M};
  border-start-end-radius: ${props => props.theme.borderRadius.M};
`

const Content = styled.div`
  position: relative;
  overflow: hidden;
  border-end-start-radius: ${props => props.theme.borderRadius.M};
  border-end-end-radius: ${props => props.theme.borderRadius.M};
  display: flex;
  flex-direction: column;
`

const Footer = styled(Group)`
  position: absolute;
  bottom: 0;
  inset-inline: 0;
  padding: ${rem(20)} ${rem(40)};
  box-shadow: ${props => props.theme.shadow.flat.small};
  background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
  z-index: 1;
  transform: translateY(100%);
  transition: transform 0.3s ${eases.expo.in};

  &.visible {
    position: sticky;
    bottom: 0;
    transform: translateY(0);
    transition: transform 0.3s ${eases.expo.out};
  }
`

const Scroller = styled.div`
  overflow: auto;
`

const HeaderButtons = styled.div`
  flex-grow: 1;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  flex-wrap: wrap;
  gap: ${rem(12)};
`

const RecommendationHeadingWrapper = styled.div`
  flex-grow: 1;
  display: flex;
  justify-content: flex-start;
  align-items: center;
`

const FetchingCalendarsIndicator = styled(LoadingIndicator)`
  padding: 0;
`

const Filters = styled.div`
  display: flex;
  position: absolute;
  margin: ${rem(-12)} ${rem(-24)} 0;
  top: 0;
  inset-inline: 0;
  padding: ${rem(12)};
  position: sticky;
  z-index: 1;
  background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
  width: calc(100% + ${rem(48)});
  justify-content: end;
  box-shadow: ${props => props.theme.shadow.bottom.small};
  gap: ${rem(8)};
  flex-wrap: wrap;
`

interface Props {
  duration: number
  fetching: boolean
  flightFetching: boolean
  flights?: boolean
  airport?: App.Airport
  offer: App.BedbankOffer
  selectedDay?: App.BedbankCalendarDay
  calendarDates: { [month: string]: App.BedbankCalendarMonth }
  isBedbankFlightBookingEnabled: boolean
  single?: boolean
  rooms?: Array<App.Occupants>
}

function CalendarDatePickerView({
  fetching,
  flightFetching,
  duration,
  single,
  selectedDay,
  calendarDates,
  rooms,
  flights,
  offer,
  isBedbankFlightBookingEnabled,
}: Props) {
  const containerRef = useRef<HTMLDivElement>(null)
  const [monthIndex, setMonth] = useState<string | undefined>()

  const intl = useIntl()
  const dispatch = useOfferPageDispatch()
  const onUserAnalyticsAction = useContext(UserAnalyticsActionContext)
  const { view: recommendationsView, close: closeRecommendations, hasRenderableRecommendations } = useCalendarRecommendationsView()
  const recommendationsShown = !!recommendationsView && shouldEnableCalendarRecommendations(offer)

  const onBackToMonth = useCallback(() => {
    if (recommendationsShown) {
      closeRecommendations()
      return
    }
    onUserAnalyticsAction?.(CALENDAR_GA_EVENT_ACTION.YearView)
    setMonth(undefined)
  }, [closeRecommendations, onUserAnalyticsAction, recommendationsShown])

  const onDatesChange = useCallback((day: App.BedbankCalendarDay) => {
    dispatch({ type: OfferPageActions.setCheckInDate, checkIn: day.checkIn })
    dispatch({ type: OfferPageActions.setCheckOutDate, checkOut: day.checkOut })
    onUserAnalyticsAction?.(CALENDAR_GA_EVENT_ACTION.DateChanged)
  }, [dispatch, onUserAnalyticsAction])

  const onMonthSelect = useCallback((monthKey: string) => {
    setMonth(monthKey)
  }, [])

  const onApply = useCallback(() => {
    onUserAnalyticsAction?.(CALENDAR_GA_EVENT_ACTION.Apply)
    dispatch({ type: OfferPageActions.closeFilter })
  }, [dispatch, onUserAnalyticsAction])

  const onDropdownClose = useCallback(() => {
    onUserAnalyticsAction?.(CALENDAR_GA_EVENT_ACTION.CloseFilter, CALENDAR_GA_EVENT_LABEL.CloseCross)
    dispatch({ type: OfferPageActions.closeFilter })
  }, [dispatch, onUserAnalyticsAction])

  const onResetDates = useCallback(() => {
    dispatch({ type: OfferPageActions.resetDates })
  }, [dispatch])

  const occupancyNumber = useMemo(() => sumUpOccupancies(rooms), [rooms])

  const adultsNumber = useMemo(() => sum(rooms ?? [], room => room.adults), [rooms])

  const flightsEnabled = offer?.flights?.flightsEnabled && isBedbankFlightBookingEnabled && occupancyNumber < 10

  const daysByMonth = useMemo(() => {
    return Object.values(calendarDates)
  }, [calendarDates])

  const monthsByYear = useMemo(() => {
    return groupBy(daysByMonth, (x) => x.year)
  }, [daysByMonth])

  const fetchingData = (flights && flightFetching) || (!flights && fetching)

  const priceCaptionWithFlights = intl.formatMessage(
    {
      id: 'Txyu6IFgxqWhwvp1zH2D',
      defaultMessage: '{duration, plural, one {# night + flights} other {# nights + flights}} from',
      description: 'Price caption showing nights count with flights on bedbank calendar date picker',
    },
    { duration },
  )
  const priceCaptionWithoutFlights = intl.formatMessage(
    {
      id: 'pEqPGwRdTzBMsGeJD1CL',
      defaultMessage: '{duration, plural, one {# night} other {# nights}} from',
      description: 'Price caption showing nights count without flights on bedbank calendar date picker',
    },
    { duration },
  )

  const hotelPrice = selectedDay?.price ?? 0
  const propertyFees = selectedDay?.propertyFees ?? 0

  const {
    total,
    saleUnit,
  } = getCalendarDisplayTotalPrice({
    price: hotelPrice + propertyFees,
    adultsNumber,
    roomsNumber: rooms?.length ?? 1,
    withFlights: !!flights,
    flightPrice: selectedDay?.flightPrice ?? 0,
    nights: duration,
    displayPricingPer: displayPricingAs(offer.displayPricingAsPerPerson),
  })
  return (<>
    {!single && !recommendationsShown && <Header direction="horizontal" horizontalAlign="space-between" verticalAlign="center" gap={12}>
      <FadingBackButton
        kind="tertiary"
        className={clsx({ visible: monthIndex !== undefined })}
        horizontalOutdent="start"
        size="large"
        onClick={onBackToMonth}
      >
        <LineAngleLeftBIcon />
      </FadingBackButton>
      <HeaderButtons>
        {fetchingData && <BodyText variant="medium" weight="bold">
          <FormattedMessage
            id="kjOvdMASGelPMLTuswR0"
            defaultMessage="Finding availability"
            description="Loading message while searching for available dates"
          />
        </BodyText>}
        {fetchingData && <FetchingCalendarsIndicator inline visible={fetchingData} />}
        <CalendarRoomSelect />
        <CalendarDurationSelect
          displayPricingAsPerPerson={offer.displayPricingAsPerPerson}
          adultsNumber={adultsNumber}
          rooms={rooms}
          flights={!!flights}
        />
        {flightsEnabled && <OfferCalendarFlightsToggle />}
      </HeaderButtons>
      <CloseButton horizontalOutdent="end" size="large" onClick={onDropdownClose} />
    </Header>}
    {
      !single && recommendationsShown && offer &&
      <Header direction="horizontal" verticalAlign="center" gap={32}>
        <FadingBackButton
          kind="tertiary"
          className={clsx({ visible: monthIndex !== undefined })}
          horizontalOutdent="start"
          size="large"
          onClick={onBackToMonth}
        >
          <LineAngleLeftBIcon />
        </FadingBackButton>
        <RecommendationHeadingWrapper>
          {hasRenderableRecommendations && <ErrorBoundary fallback={null}>
            <CalendarPickerRecommendationsHeading />
          </ErrorBoundary>}
        </RecommendationHeadingWrapper>
        <CloseButton horizontalOutdent="end" size="large" onClick={onDropdownClose} />
      </Header>
    }
    {single && !recommendationsShown && <Filters>
      <CalendarRoomSelect />
      <CalendarDurationSelect
        displayPricingAsPerPerson={offer.displayPricingAsPerPerson}
        adultsNumber={adultsNumber}
        rooms={rooms}
        flights={!!flights}
      />
      {flightsEnabled && <OfferCalendarFlightsToggle />}
    </Filters>}
    <Content>
      <Scroller ref={containerRef}>
        <Calendar>
          {recommendationsShown && offer && (
            <ErrorBoundary fallback={null}>
              <CalendarPickerRecommendations
                offerId={offer.id}
                offerType={offer.type}
                rooms={rooms}
                seedOffer={offer}
                duration={duration}
                checkIn={recommendationsView?.checkIn}
                monthIndex={recommendationsView?.monthIndex}
                onBackToMonth={single ? onBackToMonth : undefined}
              />
            </ErrorBoundary>
          )}
          {
            !recommendationsShown && (<>
              {monthIndex === undefined && <CalendarMonthPicker
                onMonthSelect={onMonthSelect}
                monthsByYear={monthsByYear}
                duration={duration}
                selectedDay={selectedDay}
                fetching={fetchingData}
                flights={!!flights}
                displayPricingAsPerPerson={offer.displayPricingAsPerPerson}
                adultsNumber={adultsNumber}
                rooms={rooms}
              />}
              {monthIndex !== undefined && !single && !fetchingData && <CalendarDayPicker
                monthIndex={monthIndex}
                daysByMonth={daysByMonth}
                contentRef={containerRef}
                duration={duration}
                onDatesChange={onDatesChange}
                fetching={fetchingData}
                flights={!!flights}
                displayPricingAsPerPerson={offer.displayPricingAsPerPerson}
                adultsNumber={adultsNumber}
                roomsNumber={rooms?.length ?? 1}
              />}
              {monthIndex !== undefined && single && !fetchingData && <CalendarSidebarDayPicker
                monthIndex={monthIndex}
                daysByMonth={daysByMonth}
                duration={duration}
                onDatesChange={onDatesChange}
                onBackToMonth={onBackToMonth}
                flights={!!flights}
                displayPricingAsPerPerson={offer.displayPricingAsPerPerson}
                adultsNumber={adultsNumber}
                roomsNumber={rooms?.length ?? 1}
              />}
            </>)
          }
        </Calendar>
      </Scroller>
      <CSSBreakpoint min="tablet">
        <Footer
          direction="horizontal"
          horizontalAlign="space-between"
          verticalAlign="center"
          className={clsx({ visible: !!selectedDay && !fetchingData })}
        >
          <TextButton kind="tertiary" size="large" onClick={onResetDates}>
            <FormattedMessage
              id="v1sUny9lcwKGntZOhVw_"
              defaultMessage="Reset dates"
              description="Button to reset selected dates on bedbank calendar date picker"
            />
          </TextButton>
          <Group direction="horizontal" verticalAlign="center" gap={32}>
            {selectedDay?.price && selectedDay?.hasPromo && <Group direction="vertical" horizontalAlign="end">
              <PriceRowPriceCaption>
                {flights ? priceCaptionWithFlights : priceCaptionWithoutFlights}
              </PriceRowPriceCaption>
              <PriceRowPrice size="M" price={total} saleUnit={saleUnit} />
            </Group>}
            <TextButton kind="primary" size="large" onClick={onApply}>
              <FormattedMessage
                id="5eZN5tiED578RK6m1IJb"
                defaultMessage="Apply"
                description="Button to apply selected dates on bedbank calendar date picker"
              />
            </TextButton>
          </Group>
        </Footer>
      </CSSBreakpoint>
    </Content>
  </>
  )
}

function CalendarDatePicker(props: Props) {
  return (
    <CalendarRecommendationsViewProvider>
      <CalendarDatePickerView {...props} />
    </CalendarRecommendationsViewProvider>
  )
}

export default withOfferPageAndReduxState((state, appState) => ({
  selectedDay: getSelectedDay(state, appState),
  calendarDates: getCalendarDates(state, appState),
  duration: state.duration,
  flights: state.flights,
  rooms: state.rooms,
  offer: state.bedbankOffer,
  fetching: isBedbankCalendarFetching(state, appState),
  flightFetching: isFlightBedbankCalendarFetching(state, appState),
  isBedbankFlightBookingEnabled: selectIsBedbankFlightBookingEnabled(appState),
}), CalendarDatePicker)
